import React, { useEffect, useState } from 'react'
import { IconContext } from 'react-icons'
import { AiFillCheckCircle, AiFillExclamationCircle, AiFillWarning } from 'react-icons/ai'
import { useGlobal } from '../../core/GlobalContext'
import { VALIDATION_STATUS } from '../../core/actions'
import { DEAL } from '../../core/constants'
import usePurchaseValidation from '../../customhooks/usePurchaseValidation'
import { inputSanitation } from '../../utils/misc'
import Button from '../button'
import Input from '../input'
import Text from '../text'
import * as styles from './DealPurchase.css'

export const DealPurchase = ({ purchase, purchaseAmount, setPurchaseAmount, buyDeal, soldOut = false }) => {
  const {
    financierState: {
      financier: { walletBalance },
    },
  } = useGlobal()

  const [purchaseLots, setPurchaseLots] = useState('')

  const purchaseValidation = usePurchaseValidation(purchaseAmount?.replace(/,/g, ''), purchase)

  const handleInputChange = val => {
    setPurchaseAmount(inputSanitation(val))
  }

  const handleLotsChange = lots => {
    let validatedLots = parseInt(lots) > purchase?.availableLots ? purchase?.availableLots : lots
    setPurchaseLots(inputSanitation(validatedLots))
    setPurchaseAmount(inputSanitation(validatedLots * purchase?.minClearedAmount))
  }

  // This is run only once during component Init
  useEffect(() => {
    if (purchase?.editable === false) {
      handleInputChange(purchase?.minClearedAmount)
      /**
     * Pre fill multiple lots input logic
     * 
     * 80 Lots 1L per Lot
    | Balance | Lots to show |    Calculation   |
    |:-------:|:------------:|:----------------:|
    | 7.6L    | 7lots        | (7.6/1=7)        |
    | 0       | 80lots       | (0/1=0)          |
    | 58k     | 80lots       | (58000/100000=0) |
     */
      if (purchase?.availableLots > 1 && purchase?.type === DEAL.DEBT) {
        handleLotsChange(
          String(
            parseInt(walletBalance / purchase?.minClearedAmount)
              ? parseInt(walletBalance / purchase?.minClearedAmount)
              : purchase?.availableLots
          )
        )
      } else if (purchase?.type === DEAL.DEBT_SECONDARY) {
        // Remove the if conditions when we start to support purchasing multiple lot purchase in secondary deals
        handleLotsChange(String(purchase?.availableLots))
      }
    } else {
      /**
       * Pre fill purchase amount input logic
       *
       * handleInputChange will validate 0 or null or '' values to undefined
       * Set minClearedAmount if minClearedAmount > walletBalance
       * Set walletBalance if walletBalance > minClearedAmount
       * Set totalAmount if walletBalance > totalAmount only for non ID deals
       * Set availableAmount if walletBalance > availableAmount for ID deals
       */
      handleInputChange(
        String(
          purchase?.minClearedAmount > walletBalance
            ? purchase?.minClearedAmount
            : !purchase?.availableAmount && walletBalance > purchase?.totalAmount
            ? purchase?.totalAmount
            : walletBalance > purchase?.availableAmount
            ? purchase?.availableAmount
            : walletBalance
        )
      )
    }
  }, [purchase, walletBalance])

  const textColor = 'var(--purchase-default-text-color)'
  const purchaseWarningIcons =
    purchaseValidation?.status === VALIDATION_STATUS.WARNING
      ? {
          color: 'var(--purchase-warning-color)',
          element: <AiFillExclamationCircle />,
          bg_color: 'var(--purchase-warning-bg-color)',
        }
      : purchaseValidation?.status === VALIDATION_STATUS.SUCCESS
      ? {
          color: 'var(--purchase-success-color)',
          element: <AiFillCheckCircle />,
          bg_color: 'var(--purchase-success-bg-color)',
        }
      : {
          color: 'var(--purchase-error-color)',
          element: <AiFillWarning />,
          bg_color: 'var(--purchase-error-bg-color)',
        }

  const handleBuyButtonPress = () => buyDeal(purchaseValidation)

  return (
    <div>
      <div className={styles.purchase}>
        <Input
          className={styles.purchaseInput}
          textStyle={styles.purchaseInputText}
          placeholder={`Enter Amount ..`}
          onChange={handleInputChange}
          value={purchaseAmount}
          number
          editable={purchase?.editable}
          showINR
        />
        {purchase?.type === DEAL.DEBT && purchase?.availableLots > 1 && !purchase?.isUnits && (
          <Input
            className={styles.lotsInput}
            placeholder={'Enter Lots ..'}
            onChange={handleLotsChange}
            number
            value={purchaseLots}
          />
        )}
        <Button
          label={soldOut ? 'CLOSED' : 'INVEST'}
          className={styles.button}
          disabled={soldOut || purchaseValidation?.status !== VALIDATION_STATUS.SUCCESS}
          handleClick={handleBuyButtonPress}
        />
      </div>
      {/* Error Validation needs to be shown here */}
      {purchaseValidation.status === VALIDATION_STATUS.IDLE ? (
        <div className={styles.purchaseError}>
          <Text className={styles.errorText}>{''}</Text>
        </div>
      ) : (
        <div className={styles.purchaseError}>
          <div className={styles.purchaseMessage} style={{ backgroundColor: purchaseWarningIcons.bg_color }}>
            <IconContext.Provider
              value={{ color: purchaseWarningIcons.color, verticalAlign: 'middle', size: '1.1rem' }}>
              {purchaseWarningIcons.element}
            </IconContext.Provider>
            <Text size='0.85' bold={purchaseValidation?.status === VALIDATION_STATUS.SUCCESS} color={textColor}>
              {purchaseValidation?.message}
            </Text>
            {purchaseValidation?.actionText && (
              <Button type='text' handleClick={purchaseValidation.action}>
                <Text size='0.85' color='var(--ib-red)'>
                  {purchaseValidation?.actionText}
                </Text>
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
